import { FormValidatorType, createForm, useFormContext } from '@components/form';
import { SelectOption } from '@components/selectmulti';
import { hasNumericValue } from '@utils/numbers';

import { AttributeType, TravellerAttributeTemplate } from 'api/attributes/requests';
import { useList } from 'api/lists/hooks';
import { TravellerAttribute } from 'api/types';
import { User } from 'api/users/requests';

type AttributeFormData = Record<string, string | File | File[] | Date | SelectOption[]>;

const Form = createForm<AttributeFormData>();

type CommonFieldProps = {
  id: string;
  label: string;
  description?: string;
  placeholder?: string;
  validators?: FormValidatorType[];
  defaultValue?: string;
};

export const AttributeFields = ({
  attributeTemplate,
  attribute,
  prefix,
  label,
  description,
  placeholder,
  usersData,
  onFileMultiRemove,
}: {
  attributeTemplate: AttributeType | TravellerAttributeTemplate;
  attribute?: TravellerAttribute;
  prefix: string;
  label: string;
  description?: string;
  placeholder?: string;
  usersData?: User[];
  onFileMultiRemove?: (file: string) => void;
}) => {
  const { store } = useFormContext();
  const usersList = usersData?.map((user) => ({ value: user.shortId, label: user.name })) ?? [];
  const listQuery = useList(attributeTemplate.options ?? '');
  const fieldId = `${prefix}${attributeTemplate.shortId}`;

  const commonFieldProps: CommonFieldProps = {
    id: fieldId,
    label,
    description,
    placeholder,
    defaultValue: attribute?.value
      ? attributeTemplate.attributeType === 'user'
        ? attribute.value?.shortId
        : attributeTemplate.attributeType === 'single_select'
          ? attribute.value.value
          : attribute.value
      : '',
    validators:
      'isRequired' in attributeTemplate && attributeTemplate.isRequired ? ['required'] : [],
  };

  switch (attributeTemplate.attributeType) {
    case 'text':
      return <Form.TextField {...commonFieldProps} />;
    case 'paragraph':
      return <Form.Textarea {...commonFieldProps} />;
    case 'date':
      return <Form.DatePicker {...commonFieldProps} placeholder="DD / MM / YYYY" />;
    case 'user':
      return <Form.Select {...commonFieldProps} options={usersList} />;
    case 'phone_number':
      return <Form.PhoneField {...commonFieldProps} />;
    case 'email':
      return (
        <Form.TextField
          {...commonFieldProps}
          validators={[...(commonFieldProps.validators ?? []), 'email']}
        />
      );
    case 'number':
    case 'decimal':
      return (
        <Form.NumberField
          {...commonFieldProps}
          defaultValue={hasNumericValue(attribute?.value) ? attribute?.value : undefined}
          isDecimal={attributeTemplate.attributeType === 'decimal'}
        />
      );
    case 'single_select':
      return (
        <Form.Select
          {...commonFieldProps}
          options={(listQuery.data?.options ?? []).map((option) => ({
            value: option.shortId,
            label: option.label,
          }))}
        />
      );
    case 'multi_select':
      const selectedOptions = (attribute?.multiOptionValues ?? []).map(
        ({ shortId, ...option }) => ({ ...option, value: shortId }),
      );
      const optionValues = store.useFieldValue(fieldId) as SelectOption[];
      const chosenOptionValues = (optionValues ?? selectedOptions).map((option) => option.value);
      const options = (listQuery.data?.options ?? []).map((option) => ({
        value: option.shortId,
        label: option.label,
      }));
      const filteredOptions = options.filter(
        (option) => !chosenOptionValues.includes(option.value),
      );
      return (
        <Form.SelectMulti
          defaultValue={selectedOptions}
          label={commonFieldProps.label}
          description={commonFieldProps.description}
          validators={commonFieldProps.validators}
          options={filteredOptions}
          // @ts-ignore
          id={fieldId}
        />
      );
    case 'file':
      const file = attribute?.files.length
        ? {
            id: attribute.files[0].id,
            name: attribute.files[0].name,
            src: attribute.files[0].src,
          }
        : undefined;
      return (
        <Form.FileField
          // @ts-ignore
          id={fieldId}
          label={commonFieldProps.label}
          validators={commonFieldProps.validators}
          defaultValue={file}
        />
      );
    case 'file_multi':
      const defultFiles = attribute?.files.length
        ? attribute.files.map((file) => ({
            id: file.id,
            name: file.name,
            src: file.src,
          }))
        : [];
      return (
        <Form.FileFieldMulti
          // @ts-ignore
          id={fieldId}
          label={commonFieldProps.label}
          defaultValue={defultFiles}
          validators={commonFieldProps.validators}
          onRemove={(file) => {
            onFileMultiRemove?.(file.id);
          }}
        />
      );
    default:
      return null;
  }
};
